/**
 * Extract Error Message Utility
 * FIX-060: [object Object] in error alerts
 *
 * Safely extracts a human-readable error message from FastAPI error responses.
 * FastAPI can return `detail` as:
 *   - A string: "Not found"
 *   - An object: { message: "Not found", code: "GOAL_NOT_FOUND" }
 *   - An array (validation errors): [{ msg: "field required", type: "missing", loc: ["body", "name"] }]
 *
 * This utility normalizes all shapes into a clean string message,
 * preventing [object Object] from appearing in user-facing error alerts.
 */

/** Maximum number of validation errors to include in a single message */
const MAX_VALIDATION_ERRORS = 5;

/** Default fallback message when no useful detail can be extracted */
const DEFAULT_FALLBACK = 'An unexpected error occurred';

/**
 * Extract a human-readable error message from an API error detail of any shape.
 *
 * @param detail - The error detail from an API response (can be string, object, array, null, etc.)
 * @param fallback - Optional custom fallback message (default: "An unexpected error occurred")
 * @returns A clean, human-readable error message string
 */
export function extractErrorMessage(
  detail: unknown,
  fallback: string = DEFAULT_FALLBACK,
): string {
  // Null/undefined -> fallback
  if (detail === null || detail === undefined) {
    return fallback;
  }

  // String -> use directly (most common case)
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  // Number/boolean -> coerce to string
  if (typeof detail === 'number' || typeof detail === 'boolean') {
    return String(detail);
  }

  // Array -> format as validation errors (FastAPI pattern)
  if (Array.isArray(detail)) {
    return formatValidationErrors(detail, fallback);
  }

  // Object -> extract message from known properties
  if (typeof detail === 'object') {
    return extractFromObject(detail as Record<string, unknown>, fallback);
  }

  // Anything else -> fallback
  return fallback;
}

/**
 * Extract a message from an object with known error properties.
 * Priority: message > msg > detail > JSON.stringify
 */
function extractFromObject(
  obj: Record<string, unknown>,
  fallback: string,
): string {
  // Try 'message' property first
  if (typeof obj.message === 'string' && obj.message.trim().length > 0) {
    return obj.message.trim();
  }

  // Try 'msg' property (FastAPI validation error format)
  if (typeof obj.msg === 'string' && obj.msg.trim().length > 0) {
    return obj.msg.trim();
  }

  // Try 'detail' property (nested detail object)
  if (obj.detail !== undefined && obj.detail !== null) {
    // Recurse one level only - if detail is itself an object with detail, just stringify
    if (typeof obj.detail === 'string') {
      const trimmed = (obj.detail as string).trim();
      if (trimmed.length > 0) return trimmed;
    } else if (typeof obj.detail === 'object' && !Array.isArray(obj.detail)) {
      const nested = obj.detail as Record<string, unknown>;
      if (typeof nested.message === 'string' && nested.message.trim().length > 0) {
        return nested.message.trim();
      }
      if (typeof nested.msg === 'string' && nested.msg.trim().length > 0) {
        return nested.msg.trim();
      }
    } else if (Array.isArray(obj.detail)) {
      return formatValidationErrors(obj.detail, fallback);
    }
  }

  // Try 'error' property
  if (typeof obj.error === 'string' && obj.error.trim().length > 0) {
    return obj.error.trim();
  }

  // Check if all known error properties are present but empty/useless
  const knownErrorKeys = ['message', 'msg', 'detail', 'error'];
  const objectKeys = Object.keys(obj);
  const hasOnlyKnownEmptyKeys = objectKeys.length > 0 && objectKeys.every(
    (key) => knownErrorKeys.includes(key) && (obj[key] === '' || obj[key] === null || obj[key] === undefined)
  );
  if (hasOnlyKnownEmptyKeys) {
    return fallback;
  }

  // Fallback: JSON.stringify (prevents [object Object])
  try {
    const json = JSON.stringify(obj);
    // Only use JSON if it's reasonably short and informative
    if (json && json !== '{}' && json.length <= 200) {
      return json;
    }
  } catch {
    // JSON.stringify can fail on circular references
  }

  return fallback;
}

/**
 * Format FastAPI validation error arrays into a human-readable string.
 *
 * FastAPI validation errors are arrays of objects like:
 * [{ msg: "field required", type: "missing", loc: ["body", "name"] }]
 *
 * Output format: "name: field required; email: invalid email"
 */
function formatValidationErrors(
  errors: unknown[],
  fallback: string,
): string {
  if (errors.length === 0) {
    return fallback;
  }

  const messages: string[] = [];

  for (const item of errors) {
    if (item === null || item === undefined) continue;

    // Handle validation error objects with msg property
    if (typeof item === 'object' && !Array.isArray(item)) {
      const err = item as Record<string, unknown>;
      const msg = typeof err.msg === 'string' ? err.msg : (typeof err.message === 'string' ? err.message : null);

      if (msg) {
        // Extract the field name from loc array (use the last element)
        const loc = Array.isArray(err.loc) ? err.loc : [];
        const fieldName = loc.length > 0 ? String(loc[loc.length - 1]) : null;

        if (fieldName) {
          messages.push(`${fieldName}: ${msg}`);
        } else {
          messages.push(msg);
        }
      }
    } else if (typeof item === 'string' && item.trim().length > 0) {
      // Handle plain string items in array
      messages.push(item.trim());
    }

    // Stop after MAX_VALIDATION_ERRORS
    if (messages.length >= MAX_VALIDATION_ERRORS) {
      break;
    }
  }

  if (messages.length === 0) {
    return fallback;
  }

  const remaining = errors.length - messages.length;
  const joined = messages.join('; ');

  if (remaining > 0) {
    return `${joined}; and ${remaining} more`;
  }

  return joined;
}
